import { SectionCardContainer } from '~/components/common/SectionCardContainer'
import type { FC, ReactNode } from 'react'
import { Alert, Box, Checkbox, FormControlLabel, Skeleton, Typography } from '@mui/material'
import { useSelectInstagramAccount } from '~/components/common/SelectInstagramAccount'
import { Stack } from '@mui/system'
import { ConnectedAtAlert } from './alert/ConnectedAtAlert'
import { ProfileViewCountSuccess } from './alert/ProfileViewCountAlert'
import {
  CompanyInstagramInsightChart,
  DummyCompanyInstagramInsightChart,
} from '~/components/common/InstagramInsight/CompanyInstagramInsightChart'
import { CompanyInstagramInsightCount } from '~/components/common/InstagramInsight/CompanyInstagramInsightCount'
import { ConnectInstagramAccountButtonWithDialog } from '~/components/common/Facebook/ConnectInstagramAccount/ConnectInstagramAccountButton'
import { useDailyInsightQuery } from '~/generated/graphql'
import { calcHomeProfileViewGrowthRate } from '~/lib/feature/calcCompanyInstagramInsight'
import { ReconnectAlert } from './alert/ReconnectAlert'
import VisibilityIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import { InstagramGrowthIcon } from '~/components/common/InstagramGrowthIcon'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { InsightPeriodSelector } from './InsightPeriodSelector'
import { DEFAULT_INSIGHT_PERIOD, formatInsightPeriodLabel, getInsightDateRange, InsightPeriod } from './periodPresets'

export const InstagramGrowthCard: FC = () => {
  const [period, setPeriod] = useState<InsightPeriod>(DEFAULT_INSIGHT_PERIOD)
  const { from, to } = useMemo(() => getInsightDateRange(period), [period])

  const { selectAccount, snsConnections, isViewerLoading, selectedInstagramAccount } = useSelectInstagramAccount()
  const snsConnectId = selectedInstagramAccount?.id ?? ''

  const { data, loading: isInsightLoading, error: insightError } = useDailyInsightQuery({
    variables: {
      from: from.toISO()!,
      to: to.toISO()!,
      snsConnectId,
    },
    skip: !snsConnectId,
  })
  const shouldReconnect = !!selectedInstagramAccount?.needTokenUpdate

  const connectedAt =
    data?.viewer.__typename === 'Advertiser' ? (data.viewer.company?.snsConnections?.[0]?.connectedAt ?? null) : null
  const showConnectedAtAlert = connectedAt != null && DateTime.fromISO(connectedAt) >= from
  const connectedAtFormatted = showConnectedAtAlert ? DateTime.fromISO(connectedAt!).toFormat('yyyy/MM/dd') : undefined

  const [showProfileViews, setShowProfileViews] = useState(true)
  const [showFollowerCount, setShowFollowerCount] = useState(true)

  const { graphData, profileViewGrowthRate, profileViewGrowthRateUnavailable, profileViewCountSum } =
    calcHomeProfileViewGrowthRate(data)

  const periodSumTitle = formatInsightPeriodLabel(from, to)
  const growthRateTooltipTitle =
    '期間の最初と最後のプロフィール閲覧数を比較した変化率です。（最初の値が0の場合は表示できません）'

  // Instagram未連携の場合
  if (!isViewerLoading && !snsConnections.length) {
    return (
      <SectionCardContainer icon={<InstagramGrowthIcon />} title="プロフィール閲覧数*フォロワー数">
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}>
            <ConnectInstagramAccountButtonWithDialog />
          </Box>
          <Typography variant="labelMJpNormalMedium" color="text.secondary">
            プロフィール閲覧数をご利用いただくには Instagram連携が必要です。
            <br />
            （連携後、情報の取得完了まで数日かかります）
          </Typography>
        </Box>
      </SectionCardContainer>
    )
  }

  return (
    <SectionCardContainer icon={<InstagramGrowthIcon />} title="プロフィール閲覧数*フォロワー数">
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <InsightPeriodSelector value={period} onChange={setPeriod} disabled={isInsightLoading || isViewerLoading} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 0, md: 4 },
            mb: 2,
          }}
        >
          <Box sx={{ flex: { xs: 'auto', md: 1 }, mb: { xs: 2, md: 0 } }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="labelSJpNormal">プロフィール閲覧数</Typography>
                </Box>
                {selectAccount}
              </Box>

              <Stack spacing={0.5}>
                <FormControlLabel
                  checked={showProfileViews}
                  onChange={(_, checked) => setShowProfileViews(checked)}
                  control={<Checkbox size="small" disabled={!graphData} />}
                  label={<Typography variant="labelSJpNormal">プロフィール閲覧数を表示</Typography>}
                />
                <FormControlLabel
                  checked={showFollowerCount}
                  onChange={(_, checked) => setShowFollowerCount(checked)}
                  control={<Checkbox size="small" disabled={!graphData} />}
                  label={<Typography variant="labelSJpNormal">フォロワー数を表示</Typography>}
                />
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: { xs: 'auto', md: 2 } }}>
            <CompanyInstagramInsightCount
              loading={isViewerLoading || isInsightLoading}
              title={periodSumTitle}
              count={profileViewCountSum}
              changeRate={profileViewGrowthRate}
              changeRateUnavailable={profileViewGrowthRateUnavailable}
              changeRateTooltipTitle={growthRateTooltipTitle}
            />
            <Stack gap={2} sx={{ mt: 2 }}>
              {shouldReconnect && <ReconnectAlert nextTokenUpdateAt={selectedInstagramAccount.nextTokenUpdateAt} />}
              {showConnectedAtAlert && (
                <ConnectedAtAlert connectedAt={DateTime.fromISO(connectedAt!).toFormat('yyyy/M/d')} />
              )}
              {profileViewGrowthRate != null && profileViewGrowthRate > 5 && (
                <ProfileViewCountSuccess rate={profileViewGrowthRate} />
              )}
            </Stack>
          </Box>
        </Box>

        <Box>
          {isInsightLoading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : isViewerLoading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : insightError ? (
            <Alert severity="error">データの取得に失敗しました。しばらく時間をおいてから再度お試しください。</Alert>
          ) : snsConnections.length && graphData ? (
            <CompanyInstagramInsightChart
              data={graphData}
              loading={isInsightLoading}
              yAxisLabel="プロフィール閲覧数"
              yAxisUnit="回"
              showProfileViews={showProfileViews}
              showFollowerCount={showFollowerCount}
              connectedAt={connectedAtFormatted}
            />
          ) : (
            <BlurredDummyGraphContainer>
              <Typography variant="labelMJpNormalMedium" color="text.invertPrimary">
                連携ありがとうございます🎉 （連携後、情報の取得完了まで数日かかります）
              </Typography>
            </BlurredDummyGraphContainer>
          )}
        </Box>
      </Box>
    </SectionCardContainer>
  )
}

const BlurredDummyGraphContainer: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'grey.800',
          p: 1.25,
          borderRadius: 2,
        }}
      >
        {children}
      </Box>

      <Box sx={{ filter: 'blur(4px)', pointerEvents: 'none' }}>
        <DummyCompanyInstagramInsightChart yAxisLabel="プロフィール閲覧数" yAxisUnit="回" />
      </Box>
    </Box>
  )
}
